import { createSelector } from 'reselect';
import { isEqual } from 'lodash';

// libraries
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import { ApplicantType } from '@makemydeal/dr-dash-types';
import type { DealHistory, ProgramFallbackAcqFee, ProgramFallbackDealerInputs, StateTree } from '@makemydeal/dr-dash-types';
import type { IDealerFee as DealerFee } from '@makemydeal/dr-platform-shared';
import type {
    IPenciledInputs,
    IPencilOfferInputs,
    ItemizedGovernmentFee,
    MenuProductsPayloadOfferDetails,
    Offer,
    OfferType,
    PaymentTerm,
    RegionBreakdown,
    ResidualInfo,
    TaxBreakdown,
    Terms,
    IVehicle as Vehicle
} from '@makemydeal/dr-platform-types';
import { CASH, LEASE } from '@makemydeal/dr-platform-types';
import type { DefaultRootState } from '@makemydeal/dr-react-18-wrappers';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { utils } from '@makemydeal/dr-offer-redux';

import * as constants from '../constants';

// selectors
import {
    getAnnualMiles,
    getBuyRateByOfferType,
    getBuyRateOverride,
    getCoBuyerInfo,
    getCreditTiers,
    getCurrentOfferType,
    getDaysToFirstPaymentBySelectedTerm,
    getDealerFees as getDealerFeesOR,
    getDealerFeesTotalFromTerm,
    getDmvFeesFromTerm,
    getDownPaymentByOfferType,
    getGovernmentFees,
    getIncentivesTotalOverride,
    getIsUserProgramQuotes,
    getMileageChargeOverride,
    getMonthlyPaymentByOfferType,
    getMonthlyPaymentRoundedByOfferType,
    getCapReductionRebate as getOfferCapReductionRebate,
    getCapReductionTradeIn as getOfferCapReductionTradeIn,
    getOfferCreationDateString,
    getOfferDetails,
    getOfferFlatTaxItem,
    getNetCashDown as getOfferNetCashDown,
    getOfferPriceByOfferType,
    getPaymentProducts,
    getRateType,
    getResidualInfoOverride,
    getRetailPriceOverride,
    getRetailPriceOverrideByOfferType,
    getTaxItemsWithOverridesWithoutFlat,
    getSecurityDepositOverride,
    getSelectedCreditTierIdByType,
    getSelectedTerm,
    getSellRateOverride,
    getShopperInfo,
    getTaxBreakdown,
    getTermIdByOfferType,
    getTypedBuyRateOverrideByOfferTypeAndTerm,
    isOfferFromVinSource,
    getDealerAcquisitionFeeUpfrontByOfferType,
    getDealerAcquisitionFeeAmountByOfferType,
    getDealerAcquisitionFeeFromTerm,
    isAcqFeeStillDealerEntered,
    hasCoBuyer,
    hasManualTotalTax,
    getItemizedLenderFees,
    getSelectedPaymentProfitDetails,
    getCashTotalAmountDueAfterCredits,
    getSelectedTermAmountFinanced,
    getMaxResidualizedMsrpOverride,
    getMaxResidualizedMsrpAppliedOverride
} from './offerRedux';

import { getManualFieldOverrides } from './manualFieldOverridesSelectors';
import { getIsError, getPaymentLocationId, isCalculatingPayment } from './payment';
import { getProgramFallbackHold, isExplicitLenderP1Active, isProgramLookupPending } from './programsSelectors';
import { getTradeIn } from './trade';

// consts/enums
import { ACQUISITION_FEE_ID } from '../constants';

export const getOfferState = (state: StateTree): Offer => {
    return state.offer || ({} as Offer);
};

export const getLeaseOutOfPocket = (state: StateTree) => {
    const selectedTerm = getSelectedTerm(state, 'lease');
    return selectedTerm.outOfPocket;
};

export const getWorkingOffer = (state: StateTree): DealHistory => {
    return {
        accessories: state.accessories || {},
        bootstrap: state.bootstrap || {},
        dealer: state.dealer || {},
        featureToggles: state.featureToggles || {},
        offer: state.offer || ({} as Offer),
        sharedBootstrap: state.sharedBootstrap || {},
        tradeIn: getTradeIn(state),
        tradeIns: state.tradeIns,
        tradeInsScoped: state.tradeInsScoped,
        vehicle: state.vehicle || ({} as Vehicle),
        vehicleProtection: state.vehicleProtection || {},
        vehicleProtectionV2: state.vehicleProtectionV2
    } as DealHistory;
};

export const hasDealBeenAccepted = (state: StateTree): boolean => {
    return getOfferState(state).accepted || false;
};

export const isOfferFromExternalSource = (state: StateTree): boolean => {
    // potentially other integrations can be grouped under this selector
    return isOfferFromVinSource(state);
};

/**
 * Internally retrieves the current offer type and in turn the relevant exact monthly payment
 */
export const getMonthlyPayment = (state: StateTree): number => {
    const currentOfferType = getCurrentOfferType(state);
    return getMonthlyPaymentByOfferType(state, currentOfferType) || 0;
};

/**
 * Internally retrieves the current offer type and in turn the relevant exact monthly payment
 */
export const getProfitDetail = (state: StateTree): number => {
    return getSelectedPaymentProfitDetails(state)?.frontEndProfit || 0;
};

/**
 * Internally retrieves the current offer type and in turn the relevant rounded monthly payment
 */
export const getMonthlyPaymentRounded = (state: StateTree): number | null => {
    const currentOfferType = getCurrentOfferType(state);
    return getMonthlyPaymentRoundedByOfferType(state, currentOfferType);
};

export const getPaymentTermsFallback = (state: StateTree) => {
    const { paymentTermsFallback } = getOfferState(state);
    return paymentTermsFallback;
};

export const getDealerFees = (state: StateTree): DealerFee[] => {
    const currentOfferType = getCurrentOfferType(state);
    if (currentOfferType === CASH) {
        return (getOfferDetails(state, CASH)?.dealerFees || []) as DealerFee[];
    } else {
        const dealerFeeSum = getDealerFeesOR(state);
        return (dealerFeeSum || []) as DealerFee[];
    }
};

export type DealerFeeWithOriginalCategory = DealerFee & {
    originalCategory: string;
    isManualFee: boolean;
    isFeeIncludedInCalculation?: boolean;
    includedInCalc?: boolean;
};

export const getIncludedCappedLenderFees = (state: StateTree): DealerFee[] => {
    const lenderFees = getItemizedLenderFees(state);
    return lenderFees.filter((fee) => fee.dealerFeeCapped && (fee.isFeeIncludedInCalculation || fee.includedInCalc));
};

export const getItemizedGovernmentFees = createSelector(
    [(state: StateTree) => getGovernmentFees(state)],
    (govFees) => govFees?.fee?.taxFee || ([] as ItemizedGovernmentFee[]),
    {
        memoizeOptions: {
            resultEqualityCheck: isEqual
        }
    }
);

export type GovernmentFeeWithIncluded = ItemizedGovernmentFee & {
    isFeeIncludedInCalculation?: boolean;
};
export const getIncludedCappedGovernmentFees = (state: StateTree): ItemizedGovernmentFee[] => {
    const governmentFees = getItemizedGovernmentFees(state);
    return governmentFees.filter(
        (fee: GovernmentFeeWithIncluded) => fee.capped && (fee.isFeeIncludedInCalculation || fee.includedInCalc)
    );
};

export const getItemizedDealerFees = createSelector(
    [(state: StateTree) => state],
    (state) => {
        const currentOfferType = getCurrentOfferType(state);
        let dealerFees = [] as DealerFeeWithOriginalCategory[];

        if (currentOfferType === CASH) {
            dealerFees = (getOfferDetails(state, CASH)?.dealerFees || []) as DealerFeeWithOriginalCategory[];
        } else {
            dealerFees = (getDealerFeesOR(state) || []) as DealerFeeWithOriginalCategory[];
        }

        return dealerFees.filter(
            (fee) => fee.originalCategory?.toLowerCase() === 'dealer' && fee.dealerFeeTypeCode !== ACQUISITION_FEE_ID
        );
    },
    {
        memoizeOptions: {
            resultEqualityCheck: isEqual
        }
    }
);

export const getIncludedCappedDealerFees = (state: StateTree): DealerFee[] => {
    const dealerFees = getItemizedDealerFees(state);
    return dealerFees.filter((fee) => fee.dealerFeeCapped && (fee.isFeeIncludedInCalculation || fee.includedInCalc));
};

export const getCombinedItemizedFees = (state: StateTree) => {
    return [...getItemizedLenderFees(state), ...getItemizedDealerFees(state), ...getItemizedGovernmentFees(state)];
};

export const getFlatOrItemizedTaxes = (state: StateTree) => {
    if (hasManualTotalTax(state)) {
        const flatTaxItem = getOfferFlatTaxItem(state);

        const advancedFlatTax = {
            ...flatTaxItem,
            taxName: 'Advanced Flat Tax'
        };
        return flatTaxItem ? [advancedFlatTax] : [];
    }
    return getTaxItemsWithOverridesWithoutFlat(state);
};

export const getFlatTaxForDisplay = (state: StateTree) => {
    const flatTaxItem = getOfferFlatTaxItem(state);

    const advancedFlatTax = {
        ...flatTaxItem,
        taxName: 'Advanced Flat Tax'
    };
    return flatTaxItem ? [advancedFlatTax] : [];
};

export const getSelectedCreditTier = (state: StateTree) => {
    const type = getCurrentOfferType(state);
    const selected = getSelectedCreditTierIdByType(state, type);
    return selected;
};

export const getCreditTiersAsOptions = createSelector(
    getCreditTiers,
    (tiers) => tiers.map((tier: any) => ({ value: tier.id, label: tier.name })),
    {
        memoizeOptions: {
            resultEqualityCheck: isEqual
        }
    }
);

export const getDealerFeeTotal = (state: StateTree): number => {
    const currentOfferType = getCurrentOfferType(state);
    if (currentOfferType === CASH) {
        return getOfferDetails(state, CASH)?.dealerFeesTotal || 0;
    } else {
        const dealerFeeSum = getDealerFeesTotalFromTerm(getSelectedTerm(state));
        return dealerFeeSum || 0;
    }
};

export const getDmvFeeTotal = (state: StateTree): number => {
    const currentOfferType = getCurrentOfferType(state);
    if (currentOfferType === CASH) {
        return getOfferDetails(state, CASH)?.dmvFees || 0;
    } else {
        const dmvFeesTotal = getDmvFeesFromTerm(getSelectedTerm(state));
        return dmvFeesTotal || 0;
    }
};

export const getDmvFeeList = (state: StateTree): any[] => {
    const governmentFee = getGovernmentFees(state);
    return governmentFee && governmentFee.fee.taxFee ? governmentFee.fee.taxFee : [];
};

export const getAmountFinanced = (state: StateTree) => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.amountFinanced || 0;
};

export const getDownPayment = (state: StateTree): number => {
    const offerType = getCurrentOfferType(state);
    return getDownPaymentByOfferType(state, offerType) || 0;
};

export const getSellingPrice = (state: StateTree) => {
    const currentOfferType = getCurrentOfferType(state);
    return getOfferPriceByOfferType(state, currentOfferType) || 0;
};

enum PaymentFrequency {
    MONTHLY = 'Monthly'
}
export const getPaymentFrequency = (_state: StateTree): PaymentFrequency => PaymentFrequency.MONTHLY;

export const getSelectedTermMonths = (state: StateTree): number | undefined => {
    const currentOfferType = getCurrentOfferType(state);
    const termId = getTermIdByOfferType(state, currentOfferType);
    if (!termId) {
        return undefined;
    }
    const parts = termId.split('_');
    return Number(parts[2]);
};

export const isValidValue = (value: number): boolean => Boolean(value) && !isNaN(value) && value >= 1;

export const isPaymentUnavailable = (state: StateTree): boolean => {
    const currentOfferType = getCurrentOfferType(state);
    const offerPrice = getSellingPrice(state);
    const monthlyPayment = getMonthlyPayment(state);
    const isCash = currentOfferType === CASH;

    const paymentHasErrored = isCash ? !isValidValue(offerPrice) : !isValidValue(monthlyPayment);
    return paymentHasErrored;
};

export const getMonthlyUseTaxBreakdown = (state: DefaultRootState): RegionBreakdown | undefined => {
    const taxBreakdown = getTaxBreakdown(state) || ({} as TaxBreakdown);
    return taxBreakdown.monthlyUseBreakdown;
};
export const getMonthlyUseTaxTotal = (state: DefaultRootState): number => {
    const taxBreakdown = getTaxBreakdown(state) || ({} as TaxBreakdown);
    return taxBreakdown.monthlyUseTotal || 0;
};
export const getPrimaryTaxTotal = (state: DefaultRootState): number => {
    const taxBreakdown = getTaxBreakdown(state) || ({} as TaxBreakdown);
    return taxBreakdown.primaryTotal;
};
export const getAdditionalTaxBreakdown = (state: DefaultRootState): RegionBreakdown => {
    const taxBreakdown = getTaxBreakdown(state) || ({} as TaxBreakdown);
    return taxBreakdown.additionalBreakdown;
};
export const getAdditionalTaxTotal = (state: DefaultRootState): number => {
    const taxBreakdown = getTaxBreakdown(state) || ({} as TaxBreakdown);
    return taxBreakdown.additionalTotal;
};

export const getShopperEmail = (state: StateTree): string => {
    const shopper = getShopperInfo(state);
    return shopper.email;
};

export const getShopperLastName = (state: StateTree): string => {
    const { lastName } = getShopperInfo(state);
    return lastName || '';
};

export const getUpfrontFeeBreakDown = (state: StateTree): any[] => {
    const dealerFees = getDealerFees(state);
    const dmvFees = getDmvFeeList(state);
    const itemizedUpfronts: any[] = [];
    dealerFees.forEach((fee) => {
        const { dealerFeeCapped, dealerFeeDesc, dealerFeeAmount } = fee;
        if (!dealerFeeCapped) {
            itemizedUpfronts.push({
                label: dealerFeeDesc,
                amount: formatDollarsAndCents(dealerFeeAmount)
            });
        }
    });
    dmvFees.forEach((fee) => {
        const { capped, description, amount } = fee;
        if (!capped) {
            itemizedUpfronts.push({
                label: description,
                amount: formatDollarsAndCents(amount)
            });
        }
    });
    return itemizedUpfronts;
};

export function getNetCashDown(state: StateTree): number {
    return getOfferNetCashDown(state) || 0;
}

export function getCapReductionRebate(state: StateTree): number {
    return getOfferCapReductionRebate(state) || 0;
}

export function getCapReductionTradeIn(state: StateTree): number {
    return getOfferCapReductionTradeIn(state) || 0;
}

export const getAvailableFinanceTermIds = (state: StateTree): string[] => {
    return state.offer?.terms.financeTerms || [];
};
export const getAvailableLeaseTermIds = (state: StateTree): string[] => {
    return state.offer?.terms.leaseTerms || [];
};

const isTermFeeUnsettled = (state: StateTree): boolean => isCalculatingPayment(state) || getIsError(state);

const getHeldDealerAcqFeeOverride = (state: StateTree, offerType: OfferType) => {
    if (offerType !== LEASE) return undefined;
    if (!isExplicitLenderP1Active(state)) return undefined;
    if (!getManualFieldOverrides(state).includes('acqFee')) return undefined;
    if (!isTermFeeUnsettled(state)) return undefined;
    const { acqFeeOverride, acqFeeUpFrontOverride } = getOfferDetails(state, offerType);
    return acqFeeOverride == null ? undefined : { acqFeeOverride, acqFeeUpFrontOverride };
};

const getHeldAcqFee = (state: StateTree, offerType: OfferType): ProgramFallbackAcqFee | undefined =>
    isProgramLookupPending(state, offerType) ? getProgramFallbackHold(state, offerType)?.acqFee : undefined;

export const isDealerAcqFeeAwaitingTerm = (state: StateTree): boolean =>
    Boolean(getHeldDealerAcqFeeOverride(state, getCurrentOfferType(state)));

export const isDealerAcqFeeInForce = (state: StateTree): boolean => {
    if (!isExplicitLenderP1Active(state)) return false;
    if (!getManualFieldOverrides(state).includes('acqFee')) return false;
    return isAcqFeeStillDealerEntered(state, getCurrentOfferType(state)) || isDealerAcqFeeAwaitingTerm(state);
};

const isAcqFeeOverridePriced = (state: StateTree, offerType: OfferType): boolean =>
    isDealerAcqFeeInForce(state) || getIsUserProgramQuotes(state, offerType);

const getPricedAcqFee = (state: StateTree, offerType: OfferType): { amount: number; upFront?: boolean } | undefined => {
    if (offerType !== LEASE || !isExplicitLenderP1Active(state)) return undefined;
    const { acqFeeOverride, acqFeeUpFrontOverride } = getOfferDetails(state, offerType);
    const termReportsFee = getDealerAcquisitionFeeFromTerm(getSelectedTerm(state, offerType)) != null;
    if (acqFeeOverride != null && isAcqFeeOverridePriced(state, offerType) && (isTermFeeUnsettled(state) || !termReportsFee)) {
        return { amount: acqFeeOverride, upFront: acqFeeUpFrontOverride };
    }
    return featureToggleSelectors.isPreserveFieldsOnPaymentResponseEnabled(state) ? undefined : getHeldAcqFee(state, offerType);
};

export function getAcqFeeOverrideWithFallback(state: StateTree): number {
    const offerType = getCurrentOfferType(state);
    const priced = getPricedAcqFee(state, offerType);
    return priced ? priced.amount : getDealerAcquisitionFeeAmountByOfferType(state, offerType);
}

export function getAcqFeeUpFrontOverrideWithFallback(state: StateTree): boolean {
    const offerType = getCurrentOfferType(state);
    const priced = getPricedAcqFee(state, offerType);
    return priced?.upFront ?? getDealerAcquisitionFeeUpfrontByOfferType(state, offerType);
}

export const getAcqFeeToCarry = (state: StateTree): ProgramFallbackAcqFee | undefined => {
    const offerType = getCurrentOfferType(state);
    const dealerEntered = isDealerAcqFeeInForce(state);
    const carried = getPricedAcqFee(state, offerType) ?? getHeldAcqFee(state, offerType);
    const amount = carried ? carried.amount : getDealerAcquisitionFeeAmountByOfferType(state, offerType);
    if (!dealerEntered && !(amount > 0)) return undefined;
    const upFront = carried?.upFront ?? getDealerAcquisitionFeeUpfrontByOfferType(state, offerType);
    return { amount, upFront, dealerEntered };
};

export const getDealerInputsToCarry = (state: StateTree): ProgramFallbackDealerInputs | undefined => {
    const recorded = getManualFieldOverrides(state);
    const { securityDepositOverride, maxResidualizedMsrp } = getOfferDetails(state, LEASE);

    const securityDeposit =
        recorded.includes('securityDeposit') && securityDepositOverride != null ? securityDepositOverride : undefined;
    const mrm =
        recorded.includes('maxResidualizedMsrp') && maxResidualizedMsrp != null && maxResidualizedMsrp > 0
            ? maxResidualizedMsrp
            : undefined;

    if (securityDeposit == null && mrm == null) return undefined;

    return {
        ...(securityDeposit != null && { securityDeposit }),
        ...(mrm != null && { maxResidualizedMsrp: mrm })
    };
};

export function getDaysToFirstPaymentWithFallback(state: StateTree): number {
    return getDaysToFirstPaymentBySelectedTerm(state) || constants.DEFAULT_DAYS_TO_FIRST_PAYMENT;
}

export function getRetailPriceOverrideWithFallback(state: StateTree): number {
    // as enableFallback param is given as true, this will always return a number
    const value = getRetailPriceOverride(state, true)!;
    return value;
}

export function getSecurityDepositOverrideWithFallback(state: StateTree): number {
    // as enableFallback param is given as true, this will always return a number
    return getSecurityDepositOverride(state, true)!;
}

export function getMaxResidualizedMsrpOverrideWithFallback(state: StateTree): number {
    return getMaxResidualizedMsrpOverride(state, true) ?? 0;
}

export function getMaxResidualizedMsrpAppliedOverrideWithFallback(state: StateTree): boolean {
    return getMaxResidualizedMsrpAppliedOverride(state, true) ?? false;
}

export function getSellRateOverrideWithFallback(state: StateTree): number {
    return getSellRateOverride(state, true) ?? 0;
}
const showsOnlyTypedBuyRate = (state: StateTree): boolean =>
    featureToggleSelectors.isBuyRatePqFirstEnabled(state) && getCurrentOfferType(state) === LEASE && !getIsUserProgramQuotes(state);

/**
 * With DE773860_Enable_Buy_Rate_PQ_First on, a lease deal on program-quotes sends only the buy rate the user typed, so the
 * field shows that rate or the one the program priced, never a stored rate the request would leave out.
 */
export function getBuyRateOverrideWithFallback(state: StateTree): number {
    if (!showsOnlyTypedBuyRate(state)) return getBuyRateOverride(state, true) ?? 0;
    return getTypedBuyRateOverrideByOfferTypeAndTerm(state) ?? getBuyRateByOfferType(state, LEASE, true) ?? 0;
}
export function getMileageChargeOverrideWithFallback(state: StateTree) {
    return getMileageChargeOverride(state, true);
}
export function getResidualInfoOverrideWithFallback(state: StateTree): ResidualInfo {
    return getResidualInfoOverride(state, true);
}

export function getIncentivesTotalOverrideWithFallback(state: StateTree): number {
    //fallback already there in offer-redux.getIncentivesTotalOverride selector
    return getIncentivesTotalOverride(state) ?? 0;
}

export function getDealRefId(state: StateTree): string {
    return state.offerInfo?.fsDealId || state.offer?.fsDealRefId || '';
}

export const getCappedDealerFeesTotal = (state: StateTree): number => {
    const dealerFees = getDealerFees(state);
    return dealerFees.reduce((total: number, dealerFee: DealerFee): number => {
        if (dealerFee.dealerFeeCapped) {
            return total + dealerFee.dealerFeeAmount;
        }
        return total;
    }, 0);
};

export const getOfferDetailsForMenu = (state: StateTree): MenuProductsPayloadOfferDetails => {
    const selectedTerm = getSelectedTerm(state);
    const offerType = getCurrentOfferType(state);
    return {
        apr: selectedTerm?.apr,
        offerType,
        term: selectedTerm?.months,
        totalFinanced: selectedTerm?.amountFinanced,
        createdOn: getOfferCreationDateString(state),
        offerPrice: getOfferPriceByOfferType(state, offerType),
        retailPrice: getRetailPriceOverrideByOfferType(state, offerType) || 0,
        monthlyPayment: getMonthlyPayment(state),
        annualMiles: getAnnualMiles(state),
        locationId: getPaymentLocationId(state),
        paymentProducts: getPaymentProducts(state, offerType) || []
    };
};

/**
 * Retrieve the rate type from either the selected term or the offer type.
 * Falls back to the dealer's defaultLeaseQuoteType setting when no rate type is set.
 * @param state
 * @returns
 */
export const getRateTypeOverrideFromOfferTypeOrSelectedTerm = (state: any) => {
    const offerType = getCurrentOfferType(state);
    const creditDecisionEnabled = getIsUserProgramQuotes(state);
    const rateType = getRateType(state);
    if (creditDecisionEnabled) {
        const offerDetails = getOfferDetails(state, offerType);
        return offerDetails.rateTypeOverride || rateType;
    }
    // Fall back to dealer's defaultLeaseQuoteType setting for lease offers when no rate type is set
    if (!rateType && offerType === LEASE) {
        const defaultLeaseQuoteType = dealerSelectors.getDefaultLeaseQuoteType(state);
        return utils.mapLeaseQuoteType(defaultLeaseQuoteType);
    }
    return rateType;
};

export const getOfferGlobalCustomerId = (state: StateTree) => {
    return getOfferState(state).globalCustomerId || '';
};

export const getOfferCommonConsumerId = (state: StateTree) => {
    return getOfferState(state).commonConsumerId || '';
};

export const getTotalAllowance = (state: StateTree) => {
    const term = getSelectedTerm(state);
    return term.totalAllowance ?? 0;
};

export const getApplicationName = (state: StateTree) => {
    return getOfferState(state).applicationName || '';
};

export const getDroppedFees = (state: StateTree) => {
    const currentOfferType = getCurrentOfferType(state);
    const offerState = getOfferState(state);
    if (currentOfferType === CASH) {
        return offerState?.penciledInputs?.byOfferType?.cash?.feesDropped || [];
    } else {
        return offerState?.payment?.feesDropped || [];
    }
};

/**
 * Retrieve the amount due as per current Offer type
 * @param state
 */
export const getBalanceDueAmountByOffer = (state: StateTree): number => {
    const currentOfferType = getCurrentOfferType(state);
    const termAmountFinanced = getSelectedTermAmountFinanced(state, currentOfferType);
    const cashAmountDue = getCashTotalAmountDueAfterCredits(state);

    return currentOfferType === CASH ? cashAmountDue ?? termAmountFinanced : termAmountFinanced;
};

export const getPaymentTerm = (
    offerType: OfferType,
    penciledInputs?: IPenciledInputs,
    terms?: Terms
): IPencilOfferInputs | PaymentTerm | undefined => {
    // TODO: replace with selector US847966
    const offerInputs = (penciledInputs as IPenciledInputs)?.byOfferType?.[offerType as OfferType];

    return offerType === CASH
        ? offerInputs
        : (() => {
              const termKey = offerInputs?.term;
              return termKey ? terms?.byId?.[termKey] : undefined;
          })();
};

export function getCommonOrgId(state: StateTree): string | undefined {
    return getOfferState(state).commonOrgId || undefined;
}

export const getIsBusinessShopper = (state: StateTree): boolean => getShopperInfo(state)?.customerType === 'Business';

export const getIsBusinessCoBuyer = (state: StateTree): boolean => getCoBuyerInfo(state)?.customerType === 'Business';

export const getApplicantType = (state: StateTree): ApplicantType => {
    const { customerType } = getShopperInfo(state);
    if (customerType !== 'Business') return ApplicantType.Consumer;
    if (!hasCoBuyer(state)) return ApplicantType.Business;

    const coBuyerParticipantRole = getCoBuyerInfo(state)?.participantRole;
    return coBuyerParticipantRole === 'Guarantor' ? ApplicantType.BusinessWithGuarantor : ApplicantType.BusinessWithCoApplicant;
};

export const getPenciledInputDiscounts = (state: StateTree) => {
    return state.offer?.penciledInputs?.dealerDiscounts || [];
};
